import type { ComponentType } from 'react';
import type { CaseStudy, CaseStudyFrontmatter } from './caseStudyTypes';

const modules = import.meta.glob('/src/content/case-studies/**/*.{md,mdx}');
let cache: CaseStudy[] | null = null;

function ensureArray<T>(value: T | T[] | undefined, fallback: T[] = []): T[] {
  if (!value) return fallback;
  return Array.isArray(value) ? value : [value];
}

function isValidFrontmatter(frontmatter: Partial<CaseStudyFrontmatter>): frontmatter is CaseStudyFrontmatter {
  return Boolean(
    frontmatter &&
    frontmatter.title &&
    frontmatter.summary &&
    frontmatter.industry &&
    frontmatter.region &&
    frontmatter.clientProfile &&
    frontmatter.challenge &&
    frontmatter.strategy
  );
}

export async function getAllCaseStudies(): Promise<CaseStudy[]> {
  if (cache) return cache;

  const caseStudies: CaseStudy[] = [];

  await Promise.all(
    Object.entries(modules).map(async ([filePath, loader]) => {
      try {
        const module = await loader() as {
          default: ComponentType<any>;
          frontmatter?: CaseStudyFrontmatter;
        };

        const { default: Component, frontmatter } = module;

        if (!frontmatter || !isValidFrontmatter(frontmatter)) {
          if (import.meta.env.DEV) {
            console.error(`Invalid case study frontmatter in ${filePath}`);
          }
          return;
        }

        const slug = frontmatter.slug || filePath.split('/').pop()?.replace(/\.(mdx|md)$/i, '') || 'case-study';
        const implementationHighlights = ensureArray(frontmatter.implementationHighlights);
        const metrics = ensureArray(frontmatter.metrics).filter(Boolean);
        const services = ensureArray(frontmatter.services);
        const segments = ensureArray(frontmatter.segments);

        caseStudies.push({
          title: frontmatter.title,
          slug,
          summary: frontmatter.summary,
          industry: frontmatter.industry,
          region: frontmatter.region,
          clientProfile: frontmatter.clientProfile,
          challenge: frontmatter.challenge,
          strategy: frontmatter.strategy,
          implementationHighlights,
          metrics: metrics as CaseStudy['metrics'],
          services,
          segments,
          publishDate: frontmatter.publishDate,
          sources: frontmatter.sources,
          testimonial: frontmatter.testimonial,
          cta: frontmatter.cta,
          compliance: frontmatter.compliance,
          Component,
        });
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error(`Failed to load case study ${filePath}`, error);
        }
      }
    })
  );

  caseStudies.sort((a, b) => {
    const dateA = a.publishDate ? new Date(a.publishDate).getTime() : 0;
    const dateB = b.publishDate ? new Date(b.publishDate).getTime() : 0;
    return dateB - dateA;
  });

  cache = caseStudies;
  return caseStudies;
}

export async function getCaseStudyBySlug(slug: string): Promise<CaseStudy | null> {
  const studies = await getAllCaseStudies();
  return studies.find(study => study.slug === slug) || null;
}

export function clearCaseStudyCache() {
  cache = null;
}
